import clsx from 'clsx';
import { useEditor } from './editorStore.js';

export default function Toolbar({
  siteName,
  onBack,
  onSave,
  onPublish,
  onOpenAi,
  onPreview,
  onOpenTheme,
  onOpenPageSettings,
}) {
  const dirty = useEditor((s) => s.dirty);
  const saving = useEditor((s) => s.saving);
  const publishing = useEditor((s) => s.publishing);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const canUndo = useEditor((s) => s.past.length > 0);
  const canRedo = useEditor((s) => s.future.length > 0);
  const device = useEditor((s) => s.device);
  const setDevice = useEditor((s) => s.setDevice);

  return (
    <header className="flex h-14 items-center gap-3 border-b border-ink-200 bg-white px-4 dark:border-ink-800 dark:bg-ink-900">
      <button
        onClick={onBack}
        className="btn-ghost flex items-center gap-1 text-sm"
        title="Back to dashboard"
      >
        ← {siteName || 'Editor'}
      </button>

      <div className="ml-2 flex items-center gap-1 rounded-lg border border-ink-200 p-0.5 dark:border-ink-800">
        <ToolButton onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">↶</ToolButton>
        <ToolButton onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">↷</ToolButton>
      </div>

      <div className="mx-auto flex items-center gap-1 rounded-lg border border-ink-200 p-0.5 dark:border-ink-800">
        {[
          ['desktop', '🖥'],
          ['tablet', '▭'],
          ['mobile', '▯'],
        ].map(([key, icon]) => (
          <button
            key={key}
            onClick={() => setDevice(key)}
            className={clsx(
              'h-8 w-10 rounded-md text-sm transition',
              device === key
                ? 'bg-brand-500 text-white'
                : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
            )}
            title={key}
          >
            {icon}
          </button>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button onClick={onOpenPageSettings} className="btn-ghost text-sm" title="Page settings (SEO, slug, home)">
          ⚙ Page
        </button>
        <button onClick={onOpenTheme} className="btn-ghost text-sm" title="Edit site theme">
          ◉ Theme
        </button>
        <button onClick={onOpenAi} className="btn-ghost text-sm" title="Generate with AI">
          ✨ AI
        </button>
        <button onClick={onPreview} className="btn-secondary text-sm">
          Preview
        </button>
        <button onClick={onSave} className="btn-secondary text-sm" disabled={saving || !dirty}>
          {saving ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
        </button>
        <button onClick={onPublish} className="btn-primary text-sm" disabled={publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>
    </header>
  );
}

function ToolButton({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className="h-8 w-8 rounded-md text-sm text-ink-600 transition hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-30 dark:text-ink-300 dark:hover:bg-ink-800"
    >
      {children}
    </button>
  );
}
